/**
 * The real Market Intelligence data source.
 *
 * Every surface now calls `/api/market/*`. The module converted one method at
 * a time behind a delegating block that fell back to the seeded generator;
 * RECs were the last to move, and the block left with them.
 *
 * `MARKET_DATA_IS_REAL` stays, all true. It was to have come out alongside
 * the block, but nothing ever read it — it was to label a panel as sample
 * data and no component asked. Removing it would delete the only record that
 * the question exists, and the next unconverted surface will want it back.
 * The generator itself stays too: `mock-source.ts` is what component tests
 * render against, and it needs no server.
 */

import { ApiError, api } from "@/lib/api";

import { regionSlug } from "./paths";

import type {
  MarketIntelligenceSource,
  MoverRow,
  OverviewCard,
  RecSeries,
  OverviewResponse,
  RegionHubSummary,
  RegionSummaryResponse,
  StripHistoryResponse,
} from "./source";
import type {
  ForwardCurveVintage,
  HourlyIndexRow,
  IndexDailyRow,
  IndexRow,
  IsoCode,
  MarketCapacity,
  MarketCommodity,
  MarketHub,
  MarketIso,
  MarketTopology,
  StripPeriod,
} from "@/types/market-intelligence";
import type { ForwardCurveDto } from "@/types/generated/ForwardCurveDto";
import type { HourlyIndexRowDto } from "@/types/generated/HourlyIndexRowDto";
import type { IndexDailyRowDto } from "@/types/generated/IndexDailyRowDto";
import type { IndexRowDto } from "@/types/generated/IndexRowDto";
import type { MarketHubDto } from "@/types/generated/MarketHubDto";
import type { MarketOverviewDto } from "@/types/generated/MarketOverviewDto";
import type { MarketTopologyDto } from "@/types/generated/MarketTopologyDto";
import type { RecSeriesDto } from "@/types/generated/RecSeriesDto";
import type { RegionHubDto } from "@/types/generated/RegionHubDto";
import type { RegionSummaryDto } from "@/types/generated/RegionSummaryDto";
import type { StripHistoryDto } from "@/types/generated/StripHistoryDto";
import type { StripPeriodDto } from "@/types/generated/StripPeriodDto";

type OverviewCardDto = MarketOverviewDto["cards"][number];

/**
 * Which surfaces are real, so the UI can say so.
 *
 * A screen mixing real strips with generated RECs otherwise reads as uniformly
 * authoritative, and these are numbers a desk may act on. Anything `false` here
 * renders its panel with a sample-data affordance.
 */
export const MARKET_DATA_IS_REAL = {
  topology: true,
  overview: true,
  strips: true,
  forwardCurve: true,
  hourly: true,
  index: true,
  recs: true,
  capacity: true,
} as const;

/**
 * The wire's `zone_kind` is the SPA's `kind`; `hasCapacity` and `inMigration`
 * are mock-era concepts the DTO does not carry.
 *
 * `inMigration` is pinned true because the server only emits hubs the feed
 * actually publishes: the flag existed to mark hubs the topology migration did
 * not know about, and that set is empty by construction now. `hasCapacity` is
 * pinned false because capacity is a property of the MARKET, not of a hub: a
 * PJM auction clears for a whole region, and the region page is where it
 * draws. Nothing reads this flag, and it goes when `MarketHub` next changes.
 *
 * `parentHub` used to be pinned null here, which silently removed the Basis
 * sub-view from every hub — `appliesTo` gates on it, so the pill and the
 * sidebar row both vanished with no error. It now comes from the wire.
 */
function toHub(dto: MarketHubDto): MarketHub {
  return {
    code: dto.code,
    name: dto.name,
    iso: dto.iso,
    commodity: dto.commodity as MarketCommodity,
    kind: dto.zone_kind as MarketHub["kind"],
    curveLocation: dto.curve_location,
    indexNode: dto.index_node,
    parentHub: dto.parent_hub,
    headline: dto.headline,
    hasCapacity: false,
    inMigration: true,
  };
}

function toTopology(dto: MarketTopologyDto): MarketTopology {
  return {
    isos: dto.isos.map(
      (i): MarketIso => ({
        code: i.code as MarketIso["code"],
        name: i.name,
        states: i.states,
      }),
    ),
    gasRegions: dto.gas_regions.map((r) => ({
      code: r.code,
      name: r.name,
      states: r.states,
    })),
    hubs: dto.hubs.map(toHub),
    latestAssessDate: dto.latest_assess_date ?? "",
  };
}

/**
 * A card's sparkline, oldest-first.
 *
 * The server sends bare prices with no per-point date, because a sparkline has
 * no x-axis — only the order is drawn. `assess_date` is therefore empty rather
 * than interpolated: inventing dates here would put wrong values in the chart
 * data dialog, which reads the same points.
 */
function toSparkSeries(dto: OverviewCardDto): OverviewCard["series"] {
  return dto.spark.map((price) => ({
    assess_date: "",
    num_months: 12,
    on_peak_price: null,
    off_peak_price: null,
    atc_price: price,
  }));
}

function toCard(dto: OverviewCardDto): OverviewCard {
  return {
    hub: dto.hub,
    iso: dto.iso,
    measure: dto.measure === "daily_index" ? "daily_index" : "strip_12m",    series: toSparkSeries(dto),
    stats: {
      hubName: dto.hub_name,
      asOf: dto.as_of,
      current: dto.current,
      changeDay: dto.change_day,
      percentile: dto.percentile,
    },
  };
}

/**
 * The wire is snake_case; `IndexRow` is the SPA's hand-written camelCase type.
 *
 * The two conventions coexist because `StripPoint` is generated (so snake_case
 * reaches the models unchanged) while `IndexRow` predates the generator. This
 * is the one place that gap is bridged.
 */
function toIndexRow(dto: IndexRowDto): IndexRow {
  return {
    periodStart: dto.period_start,
    daPriceAtc: dto.da_price_atc,
    daPriceOnPeak: dto.da_price_on_peak,
    daPriceOffPeak: dto.da_price_off_peak,
    rtPriceAtc: dto.rt_price_atc,
    rtPriceOnPeak: dto.rt_price_on_peak,
    rtPriceOffPeak: dto.rt_price_off_peak,
    daCongestionAtc: dto.da_congestion_atc,
    rtCongestionAtc: dto.rt_congestion_atc,
    hoursPresent: dto.hours_present,
    hoursExpected: dto.hours_expected,
  };
}

function toRegionHub(dto: RegionHubDto): RegionHubSummary {
  return {
    hub: dto.hub,
    strip12m: dto.strip12m,
    latestIndex: dto.latest_index ? toIndexRow(dto.latest_index) : null,
    // Carried from the server so the models never fall back to the static
    // topology, which does not know the hubs the feed publishes.
    name: dto.name,
    kind: dto.zone_kind,
    tenors: dto.tenors.map((t) => ({
      numMonths: t.num_months,
      low: t.low,
      high: t.high,
      avg: t.avg,
      current: t.current,
    })),
    forwardQuoted: dto.forward_quoted,
    settledMonthly: dto.settled_monthly,
    basis: dto.basis
      ? {
          parentHub: dto.basis.parent_hub,
          parentName: dto.basis.parent_name,
          periodStart: dto.basis.period_start,
          daBasis: dto.basis.da_basis,
        }
      : null,
  };
}

function toMover(dto: OverviewCardDto): MoverRow {
  return {
    hub: dto.hub,
    hubName: dto.hub_name,
    iso: dto.iso,
    current: dto.current,
    changeDay: dto.change_day,
    changeWeek: dto.change_week,
    changeMonth: dto.change_month,
    changeYear: dto.change_year,
  };
}

export const httpMarketIntelligenceSource: MarketIntelligenceSource = {
  async getRecSeries(q): Promise<RecSeries[]> {
    const params = new URLSearchParams();
    if (q.iso) params.set("iso", q.iso);
    if (q.from) params.set("from", q.from);
    const query = params.toString();

    try {
      const rows = await api.get<RecSeriesDto[]>(
        `/api/market/recs/series${query ? `?${query}` : ""}`,
      );
      return rows.map((row) => ({
        certificateId: row.certificate_id,
        name: row.name,
        iso: row.iso as IsoCode,
        points: row.points.map((p) => ({ assessDate: p.assess_date, price: p.price })),
      }));
    } catch (err) {
      // A market with no named certificate answers 404, the same as a typo'd
      // ISO. Both mean "there is no program here", which the page renders
      // as its empty state rather than as a failure to load one.
      if (err instanceof ApiError && err.status === 404) return [];
      throw err;
    }
  },

  async getStripPeriods(q): Promise<StripPeriod[]> {
    const rows = await api.get<StripPeriodDto[]>(
      `/api/market/hubs/${encodeURIComponent(q.hub)}/strip-periods`,
    );
    return rows.map((r) => ({
      periodKind: r.period_kind as StripPeriod["periodKind"],
      periodYear: r.period_year,
      periodQuarter: r.period_quarter,
      assessDates: r.assess_dates,
      firstQuoted: r.first_quoted,
      lastQuoted: r.last_quoted,
    }));
  },

  /**
   * ONE vintage, where the generator sent two.
   *
   * `asOfDates` is accepted and ignored: the mirror holds only today's curve,
   * so the "90 days ago" comparison curve the generator drew does not exist to
   * fetch. The server starts it at next month (AB#8508), so no settled or
   * current month arrives here. Returning one element keeps the
   * chart's `data.map((v, i) => …)` working — it draws the first as the current
   * curve and the rest as dashed priors, so one in means one line.
   */
  async getForwardCurve(q): Promise<ForwardCurveVintage[]> {
    const dto = await api.get<ForwardCurveDto>(
      `/api/market/hubs/${encodeURIComponent(q.hub)}/forward-curve`,
    );
    if (dto.assess_date === null || dto.points.length === 0) return [];
    return [
      {
        assessDate: dto.assess_date,
        points: dto.points.map((p) => ({
          deliveryMonth: p.delivery_month,
          onPeakPrice: p.on_peak_price,
          offPeakPrice: p.off_peak_price,
          atcPrice: p.atc_price,
        })),
      },
    ];
  },

  async getHourlyIndex(q): Promise<HourlyIndexRow[]> {
    const rows = await api.get<HourlyIndexRowDto[]>(
      `/api/market/hubs/${encodeURIComponent(q.hub)}/index-hourly?days=${q.days ?? 14}`,
    );
    return rows.map((r) => ({
      hourStart: r.hour_start,
      hourLocal: r.hour_local,
      zone: r.zone,
      daPrice: r.da_price,
      rtPrice: r.rt_price,
    }));
  },

  async getRegionSummary(q): Promise<RegionSummaryResponse> {
    // The region's url slug, never its code: a gas region's code can carry a
    // slash ("Louisiana/Southeast"), and an encoded slash in a path is decoded
    // by some proxies before the router sees it. The server matches either.
    const dto = await api.get<RegionSummaryDto>(
      `/api/market/regions/${regionSlug(q.iso)}/summary?commodity=${encodeURIComponent(
        q.commodity,
      )}`,
    );
    return {
      // The server's own spelling, so a caller that sent a slug gets back the
      // code its points carry.
      iso: dto.iso,
      asOf: dto.as_of ?? "",
      window: dto.window,
      hubs: dto.hubs.map(toRegionHub),
    };
  },

  async getStripHistory(q): Promise<StripHistoryResponse> {
    // Rolling sends no mode and gets all five tenors, which the chart filters
    // client-side by `num_months`. Calendar and quarterly name their period:
    // those come from a different table (`strip_fixed`), because a rolling
    // 12-month strip covers different months every day while Cal 27 is the same
    // twelve whenever it is quoted.
    const params = new URLSearchParams({ from: q.from });
    if (q.everyDay) params.set("every_day", "true");
    if (q.spec.mode === "calendar") {
      params.set("mode", "calendar");
      params.set("year", String(q.spec.year));
    } else if (q.spec.mode === "quarterly") {
      params.set("mode", "quarterly");
      params.set("year", String(q.spec.year));
      params.set("quarter", String(q.spec.quarter));
    }
    const dto = await api.get<StripHistoryDto>(
      `/api/market/hubs/${encodeURIComponent(q.hub)}/strips?${params}`,
    );
    return {
      hub: dto.hub,
      commodity: dto.commodity as MarketCommodity,
      unit: dto.unit,
      window: dto.window,
      points: dto.points,
    };
  },

  async getIndexSeries(q): Promise<IndexRow[]> {
    const rows = await api.get<IndexRowDto[]>(
      `/api/market/hubs/${encodeURIComponent(q.hub)}/index?months=${q.months ?? 36}`,
    );
    return rows.map(toIndexRow);
  },

  async getIndexDaily(q): Promise<IndexDailyRow[]> {
    const rows = await api.get<IndexDailyRowDto[]>(
      `/api/market/hubs/${encodeURIComponent(q.hub)}/index-daily?from=${q.from}&to=${q.to}`,
    );
    return rows.map((r) => ({
      day: r.day,
      daAtc: r.da_atc,
      daOnPeak: r.da_on_peak,
      daOffPeak: r.da_off_peak,
      rtAtc: r.rt_atc,
      rtOnPeak: r.rt_on_peak,
      rtOffPeak: r.rt_off_peak,
      nymexFrontMonth: r.nymex_front_month,
    }));
  },

  async getTopology(): Promise<MarketTopology> {
    return toTopology(await api.get<MarketTopologyDto>("/api/market/topology"));
  },

  async getOverview(q): Promise<OverviewResponse> {
    // ONE request. This used to fan out to three, adding a full REC series and
    // the capacity pool alongside the overview itself — but the panels that
    // read them came off this page in the same change that added real REC
    // data, and nothing consumes `recs` or `capacity` any more.
    //
    // The REC one was the expensive half: `latestRecPrices()` asked for every
    // named certificate across all four ISOs, unscoped, at the server's
    // 400-day default — roughly a thousand points fetched on every visit to
    // the module's landing page, parsed, and thrown away. The region RECs page
    // fetched the same certificates again, ISO-scoped, for its own chart.
    //
    // `recs` and `capacity` stay on `OverviewResponse` as empty arrays rather
    // than being removed from the type: the mock and test sources still
    // populate them, and the shape is what a future panel would read.
    const dto = await api.get<MarketOverviewDto>(
      `/api/market/overview?commodity=${encodeURIComponent(q.commodity)}`,
    );

    return {
      asOf: dto.as_of ?? "",
      commodity: dto.commodity as MarketCommodity,
      window: dto.window,
      cards: dto.cards.map(toCard),
      // Every point a region card steps through, as the full card DTO: price,
      // sparkline, percentile. Its own list, because the movers leave out the
      // index-only points a settled-only region's card steps through (AB#8626).
      points: dto.points.map(toCard),
      movers: dto.movers.map(toMover),
      recs: [],
      capacity: [],
    };
  },

  async getCapacity(q): Promise<MarketCapacity | null> {
    try {
      return await api.get<MarketCapacity>(
        `/api/market/regions/${encodeURIComponent(q.iso.toLowerCase())}/capacity`,
      );
    } catch (err) {
      // A market that clears no auction answers 404, the same as a typo'd
      // ISO. Both mean "there is nothing to draw here", which is the page's
      // empty state rather than its error state.
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
};

